import React, { useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';

import Header from './components/Header';
import Footer from './components/Footer';
import KakaoCallback from './pages/KakaoCallback';

const AUTO_LOGOUT_TIME = 1 * 60 * 60 * 1000; // 1시간
const API_URL = process.env.REACT_APP_API_URL || 'https://campus-life-production.up.railway.app';

function PrivateAdminRoute({ children }) {
  const userData = localStorage.getItem('currentUser');
  if (!userData) return <LoginPage />;
  try {
    const user = JSON.parse(userData);
    if (user.email !== '20232370@usk.ac.kr') return <LoginPage />;
  } catch(e) {
    return <LoginPage />;
  }
  return children;
}

import LoginPage from './pages/LoginPage';
import MainPage from './pages/MainPage';
import UploadPage from './pages/UploadPage';
import SummaryPage from './pages/SummaryPage';
import QuizPage from './pages/QuizPage';
import PlannerPage from './pages/PlannerPage';
import AIAssistantPage from './pages/AIAssistantPage';
import TeamProjectPage from './pages/TeamProjectPage';
import TeamManagementPage from './pages/TeamManagementPage';
import LadderGamePage from './pages/LadderGamePage';
import DrawLotsPage from './pages/DrawLotsPage';
import RandomAssignPage from './pages/RandomAssignPage';
import RoulettePage from './pages/RoulettePage';
import FindPwPage from './pages/FindPwPage';
import RegisterPage from './pages/RegisterPage';
import NoticeBoard from './pages/NoticeBoard';
import CoverLetterManager from './pages/CoverLetterManager';
import AdminPage from './pages/AdminPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import JoinPage from './pages/JoinPage';
import VerifyPage from './pages/VerifyPage';
import FindPwKakaoCallback from './pages/FindPwKakaoCallback';

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();

  const hideHeaderPaths = ['/', '/register', '/find-pw', '/admin', '/verify', '/auth/kakao/callback', '/auth/kakao/findpw-callback'];
  const shouldHideHeader = hideHeaderPaths.includes(location.pathname);
  const shouldHideFooter = hideHeaderPaths.includes(location.pathname);

  // ✅ 자동 로그아웃
  const handleLogout = useCallback((message) => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    if (message) alert(message);
    navigate('/');
  }, [navigate]);

  // ✅ 자동 로그아웃 타이머
  useEffect(() => {
    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      const token = localStorage.getItem('token');
      if (token) {
        timer = setTimeout(() => handleLogout('1시간 동안 활동이 없어 자동 로그아웃 되었습니다.'), AUTO_LOGOUT_TIME);
      }
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [handleLogout]);

  // ✅ 세션 검증 (페이지 접속/이동 시마다)
  useEffect(() => {
    const verifySession = async () => {
      const token = localStorage.getItem('token');
      const publicPaths = ['/', '/register', '/find-pw', '/verify', '/auth/kakao/callback', '/auth/kakao/findpw-callback', '/join', '/invite'];
      console.log('🔍 verifySession 실행:', location.pathname, '토큰:', token ? '있음' : '없음');
      if (publicPaths.some(p => location.pathname === p || (p !== '/' && location.pathname.startsWith(p)))) {
        console.log('🔓 공개 페이지 - 검증 스킵');
        return;
      }
      if (!token) {
        console.log('❌ 토큰 없음 - 로그아웃');
        handleLogout(null);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/auth/verify`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!res.ok) {
          handleLogout('세션이 만료되었거나 유효하지 않습니다. 다시 로그인해주세요.');
        } else {
          // 검증 성공 → currentUser 최신화
          const data = await res.json();
          const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
          if (String(currentUser.userId) !== String(data.userId)) {
            // 사용자 불일치 → 강제 로그아웃
            handleLogout('비정상적인 접근이 감지되었습니다. 다시 로그인해주세요.');
          }
        }
      } catch {
        // 서버 오류면 그냥 통과 (서버 다운 시 무조건 로그아웃 방지)
      }
    };

    verifySession();
  }, [location.pathname, handleLogout]);

  return (
    <>
      {!shouldHideHeader && <Header />}
      <div style={{ paddingTop: shouldHideHeader ? '0' : '60px' }}>
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/main" element={<MainPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/summary" element={<SummaryPage />} />
          <Route path="/quiz" element={<QuizPage />} />
          <Route path="/planner" element={<PlannerPage />} />
          <Route path="/ai-assistant" element={<AIAssistantPage />} />
          <Route path="/team-project" element={<TeamProjectPage />} />
          <Route path="/team-management" element={<TeamManagementPage />} />
          <Route path="/ladder-game" element={<LadderGamePage />} />
          <Route path="/draw-lots" element={<DrawLotsPage />} />
          <Route path="/random-assign" element={<RandomAssignPage />} />
          <Route path="/roulette" element={<RoulettePage />} />
          <Route path="/find-pw" element={<FindPwPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/notice-board" element={<NoticeBoard />} />
          <Route path="/cover-letter" element={<CoverLetterManager />} />
          <Route path="/admin" element={<PrivateAdminRoute><AdminPage /></PrivateAdminRoute>} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/invite/team/:teamId" element={<InviteAcceptPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/auth/kakao/callback" element={<KakaoCallback />} />
          <Route path="/auth/kakao/findpw-callback" element={<FindPwKakaoCallback />} />
        </Routes>
      </div>
      {!shouldHideFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;